import type { ReactNode } from "react";

import { useBackend } from "../app/BackendProvider";
import { describeError } from "../app/useResource";
import {
  formatAgo,
  formatDeviationHms,
  formatIsoTimeSeconds,
  positionTypeLabel,
  runMatchState,
  shortVehicleNumber,
} from "../app/format";
import { useNetworkId } from "../config/ConfigProvider";
import { DeviationBadge, LineBadge, PositionDot } from "./bits";
import { usePolledResource } from "./hooks";

/**
 * Détail de la course sélectionnée (F1.6). Identité STRICTEMENT non nominative :
 * numéro de bus, sortie, numéro de service — jamais de nom, jamais de matricule.
 * Pas d'indicateur de charge tant que `occupancy` n'a pas de source (D-005).
 *
 * Rafraîchie (20 s, comme les autres outils du poste — `usePolledResource`) :
 * la « dernière position » se lisait en absolu (`formatIsoTime`), donc restait
 * correcte même figée après un chargement unique. Depuis qu'elle s'affiche en
 * relatif (« il y a X », demande utilisateur), un chargement figé l'aurait
 * rendue fausse avec le temps qui passe — d'où le passage en polling ici aussi.
 */
export function JourneyDetailPanel({ journeyId }: { journeyId: string | null }) {
  const backend = useBackend();
  const networkId = useNetworkId();

  const { data, error, loading } = usePolledResource(
    (signal) => (journeyId ? backend.api.getJourney(networkId, journeyId, signal) : Promise.resolve(null)),
    [backend, networkId, journeyId],
    20_000,
  );

  if (!journeyId) {
    return (
      <div>
        <p className="has-text-grey is-size-7">Sélectionner un écart ou un véhicule.</p>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div>
        <p className="has-text-grey is-size-7">Chargement…</p>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div>
        <p className="has-text-danger is-size-7">{error ? describeError(error) : "Course introuvable."}</p>
      </div>
    );
  }

  const run = runMatchState(data.runId, data.runConfidence);
  const { destination, ville } = splitHeadsign(data.headsign);

  return (
    <div>
      {/* §J (dump 2026-09-10) — « Détail course » retiré, redondant avec le
          contenu qui suit. Le badge d'écart migre en aside de la ligne
          d'identité. J.6 — le code ligne n'est plus répété en préfixe :
          `LineBadge` le porte déjà. Destination sur une ligne, VILLE (mots en
          capitales en fin de girouette) sur la ligne du dessous. */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <LineBadge lineId={data.lineId} size={38} />
          <div>
            <div style={{ font: "700 15px/1.15 var(--bulma-family-secondary)", color: "var(--sentinelle-ink)" }}>
              {destination}
            </div>
            {ville ? (
              <div className="ovl" style={{ marginTop: 1 }}>
                {ville}
              </div>
            ) : null}
          </div>
        </div>
        <DeviationBadge seconds={data.deviationSeconds} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", marginTop: 13, borderTop: "1px solid var(--sentinelle-rule)" }}>
        <Cell label="Sortie">
          <span className="mono" style={{ fontSize: "1rem", fontWeight: 700 }}>
            {/* Le numéro nu (« 04 »), pas `runId` complet (« F5-09 ») — la ligne
                est déjà portée par le badge à gauche, inutile de la répéter. */}
            {run.matched ? (data.runNumber ?? run.text) : <span className="has-text-grey is-size-7">{run.text}</span>}
          </span>
        </Cell>
        <Cell label="Numéro" border>
          <span className="mono" style={{ fontSize: "1rem", fontWeight: 700 }}>
            {data.vehicle ? shortVehicleNumber(data.vehicle.displayNumber) : <span className="has-text-grey is-size-7">?</span>}
          </span>
        </Cell>
        <Cell label="Position" border>
          <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9rem", fontWeight: 600 }}>
            <PositionDot type={data.positionType} />
            {positionTypeLabel(data.positionType)}
          </span>
          {data.positionAt ? (
            <span className="ovl" style={{ display: "block", marginTop: 2 }}>
              il y a {formatAgo(data.positionAt)}
            </span>
          ) : null}
        </Cell>
      </div>

      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          gap: "4px 12px",
          margin: "12px 0 0",
          fontSize: "0.8rem",
        }}
      >
        <dt className="ovl">Service</dt>
        <dd className="mono" style={{ margin: 0 }}>
          {data.serviceId ?? <span className="has-text-grey">—</span>}
        </dd>
      </dl>

      <div className="ovl" style={{ margin: "14px 0 6px" }}>Prochains arrêts</div>
      <table className="table is-fullwidth is-narrow" style={{ fontSize: "11px", background: "var(--sentinelle-surface)" }}>
        <thead>
          <tr>
            <th className="ovl">Arrêt</th>
            <th className="ovl has-text-right">Théorique</th>
            <th className="ovl has-text-right">Estimé</th>
            <th className="ovl has-text-right">Impact</th>
          </tr>
        </thead>
        <tbody>
          {data.nextStops.slice(0, 6).map((stop) => (
            <tr key={stop.stopId} style={stop.skipped ? { opacity: 0.5, textDecoration: "line-through" } : undefined}>
              <td>{stop.name}</td>
              <td className="mono has-text-right has-text-grey">{formatIsoTimeSeconds(stop.scheduled)}</td>
              <td className="mono has-text-right">
                {stop.expected ? formatIsoTimeSeconds(stop.expected) : "—"}
              </td>
              <td className="mono has-text-right">
                {stop.expected ? formatDeviationHms(deltaSeconds(stop.scheduled, stop.expected)) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ label, children, border }: { label: string; children: ReactNode; border?: boolean }) {
  return (
    <div style={{ padding: "10px 12px 0", borderLeft: border ? "1px solid var(--sentinelle-rule)" : undefined }}>
      <div className="ovl">{label}</div>
      <div style={{ marginTop: 5 }}>{children}</div>
    </div>
  );
}

function deltaSeconds(scheduledIso: string, expectedIso: string): number {
  return Math.round((new Date(expectedIso).getTime() - new Date(scheduledIso).getTime()) / 1000);
}

/**
 * Girouette « Destination COMMUNE » → destination + commune séparées (§G / J.6) :
 * la commune = suite de mots en CAPITALES en fin de girouette. Rien à isoler ⇒
 * `ville` nul, la girouette entière tient lieu de destination.
 */
function splitHeadsign(headsign: string): { destination: string; ville: string | null } {
  const m = /^(.*\S)\s+([A-ZÀ-Ÿ][A-ZÀ-Ÿ' -]{1,})$/.exec(headsign.trim());
  return m ? { destination: m[1]!, ville: m[2]! } : { destination: headsign, ville: null };
}
